import {useUserPreferencesHydration} from '@common'
import {NavigationPathKey, UserRole} from '@enums'
import {PrivateRoutes} from '@routes'
import {AppWrapper, PageWrapper} from '@wrappers'

import React, {useEffect} from 'react'

import {preloadAllImages} from 'common/preload-images'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'

import {Pages} from '@components'

import './App.css'

function App() {
  useEffect(() => {
    preloadAllImages()
  }, [])
  useUserPreferencesHydration()
  return (
    <div className="App">
      <AppWrapper>
        <BrowserRouter>
          <Routes>
            <Route path="*" element={<Pages.NotFoundPage />} />
            <Route path="/" element={<Navigate to={NavigationPathKey.LOGIN} />} />

            {/* Authentication */}
            <Route path={NavigationPathKey.LOGOUT} element={<Pages.LogoutPage />} />
            <Route path={NavigationPathKey.LOGIN} element={<Pages.LoginPage />} />
            <Route path={NavigationPathKey.CALLBACK} element={<Pages.CallbackPage />} />

            {/* All Users */}
            <Route
              element={
                <PrivateRoutes
                  allowedRoles={[
                    UserRole.SUPER_ADMIN,
                    UserRole.COMPANY_ADMIN,
                    UserRole.COMPANY_USER,
                    UserRole.PARENT_ADMINISTRATOR,
                  ]}
                />
              }
            >
              <Route element={<PageWrapper />}>
                <Route path={NavigationPathKey.DASHBOARD} element={<Pages.DashboardPage />} />

                {/* Companies */}
                <Route path={NavigationPathKey.COMPANY_SEARCH} element={<Pages.CompanySearchPage />} />
                <Route path={`${NavigationPathKey.COMPANY_VIEW}/:id`} element={<Pages.CompanyViewPage />} />

                {/* Parent Companies  */}
                <Route path={NavigationPathKey.PARENT_COMPANY_SEARCH} element={<Pages.ParentCompanySearchPage />} />
                <Route
                  path={`${NavigationPathKey.PARENT_COMPANY_VIEW}/:id`}
                  element={<Pages.ParentCompanyViewPage />}
                />

                {/* Dealers */}
                <Route path={NavigationPathKey.DEALER_SEARCH} element={<Pages.DealerSearchPage />} />
                <Route path={`${NavigationPathKey.DEALER_VIEW}/:id`} element={<Pages.DealerViewPage />} />

                {/* Contract types */}
                <Route path={NavigationPathKey.CONTRACT_TYPE_SEARCH} element={<Pages.ContractTypeSearchPage />} />
                <Route path={`${NavigationPathKey.CONTRACT_TYPE_VIEW}/:id`} element={<Pages.ContractTypeViewPage />} />

                {/* Users */}
                <Route path={NavigationPathKey.USER_SEARCH} element={<Pages.UserSearchPage />} />
                <Route path={`${NavigationPathKey.USER_CREATION}/:id`} element={<Pages.UserFormPage />} />
                <Route path={`${NavigationPathKey.USER_VIEW}/:id`} element={<Pages.UserViewPage />} />

                {/* Products */}
                <Route path={NavigationPathKey.PRODUCT_SEARCH} element={<Pages.ProductSearchPage />} />
                <Route path={`${NavigationPathKey.PRODUCT_VIEW}/:id`} element={<Pages.ProductViewPage />} />

                {/* Accessories */}
                <Route path={NavigationPathKey.ACCESSORY_SEARCH} element={<Pages.AccessorySearchPage />} />
                <Route path={`${NavigationPathKey.ACCESSORY_VIEW}/:id`} element={<Pages.AccessoryViewPage />} />

                {/* Settings */}
                <Route path={NavigationPathKey.SETTINGS} element={<Pages.SettingsPage />} />

                {/* Contracts */}
                <Route path={NavigationPathKey.CONTRACT_SEARCH} element={<Pages.ContractSearchPage />} />
                <Route path={NavigationPathKey.CONTRACT_CREATION} element={<Pages.ContractFormPage />} />
                <Route path={`${NavigationPathKey.CONTRACT_CREATION}/:id`} element={<Pages.ContractFormPage />} />
                <Route path={`${NavigationPathKey.CONTRACT_VIEW}/:id`} element={<Pages.ContractViewPage />} />
                <Route
                  path={`${NavigationPathKey.CONTRACT_LEGACY_VIEW}/:id`}
                  element={<Pages.ContractLegacyViewPage />}
                />
              </Route>
            </Route>

            {/* Administrators Only */}
            <Route
              element={
                <PrivateRoutes
                  allowedRoles={[UserRole.SUPER_ADMIN, UserRole.COMPANY_ADMIN, UserRole.PARENT_ADMINISTRATOR]}
                />
              }
            >
              <Route element={<PageWrapper />}>
                {/* Companies */}
                <Route path={`${NavigationPathKey.COMPANY_CREATION}/:id`} element={<Pages.CompanyFormPage />} />

                {/* Parent Companies */}
                <Route
                  path={`${NavigationPathKey.PARENT_COMPANY_CREATION}/:id`}
                  element={<Pages.ParentCompanyFormPage />}
                />

                {/* Dealers */}
                <Route path={NavigationPathKey.DEALER_CREATION} element={<Pages.DealerFormPage />} />
                <Route path={`${NavigationPathKey.DEALER_CREATION}/:id`} element={<Pages.DealerFormPage />} />

                {/* Contract types */}
                <Route path={NavigationPathKey.CONTRACT_TYPE_CREATION} element={<Pages.ContractTypeFormPage />} />
                <Route
                  path={`${NavigationPathKey.CONTRACT_TYPE_CREATION}/:id`}
                  element={<Pages.ContractTypeFormPage />}
                />

                {/* Users */}
                <Route path={NavigationPathKey.USER_CREATION} element={<Pages.UserFormPage />} />

                {/* Products */}
                <Route path={NavigationPathKey.PRODUCT_CREATION} element={<Pages.ProductFormPage />} />
                <Route path={`${NavigationPathKey.PRODUCT_CREATION}/:id`} element={<Pages.ProductFormPage />} />
                <Route path={NavigationPathKey.PRODUCT_BULK_CREATION} element={<Pages.BulkProductUploadPage />} />

                {/* Accessories */}
                <Route path={NavigationPathKey.ACCESSORY_CREATION} element={<Pages.AccessoryFormPage />} />
                <Route path={`${NavigationPathKey.ACCESSORY_CREATION}/:id`} element={<Pages.AccessoryFormPage />} />

                {/* Logs */}
                <Route path={NavigationPathKey.LOGS} element={<Pages.LogsSearchPage />} />

                {/* Admin Panel */}
                <Route path={NavigationPathKey.ADMIN_PANEL} element={<Pages.AdminDashboardPage />} />
                <Route
                  path={NavigationPathKey.PRODUCT_DASHBOARD}
                  element={<Pages.ProductAndAccessoriesDashboardPage />}
                />
                <Route path={NavigationPathKey.CONTRACT_DASHBOARD} element={<Pages.ContractsDashboardPage />} />
                <Route path={NavigationPathKey.RESOURCES_DASHBOARD} element={<Pages.ResourcesDashboardPage />} />
              </Route>
            </Route>

            {/* Super Administrator Only */}
            <Route element={<PrivateRoutes allowedRoles={[UserRole.SUPER_ADMIN]} />}>
              <Route element={<PageWrapper />}>
                {/* Companies */}
                <Route path={NavigationPathKey.COMPANY_CREATION} element={<Pages.CompanyFormPage />} />

                {/* Parent Companies */}
                <Route path={NavigationPathKey.PARENT_COMPANY_CREATION} element={<Pages.ParentCompanyFormPage />} />

                {/* Contract templates */}
                <Route
                  path={NavigationPathKey.CONTRACT_TEMPLATE_SEARCH}
                  element={<Pages.ContractTemplateSearchPage />}
                />
                <Route
                  path={`${NavigationPathKey.CONTRACT_TEMPLATE_CREATION}/:id`}
                  element={<Pages.ContractTemplateFormPage />}
                />
                <Route
                  path={NavigationPathKey.CONTRACT_TEMPLATE_CREATION}
                  element={<Pages.ContractTemplateFormPage />}
                />
                <Route
                  path={`${NavigationPathKey.CONTRACT_TEMPLATE_VIEW}/:id`}
                  element={<Pages.ContractTemplateViewPage />}
                />

                {/* Credit decision configuration */}
                <Route path={NavigationPathKey.CREDIT_DECISION} element={<Pages.CreditDecisionViewPage />} />
                <Route path={NavigationPathKey.CREDIT_DECISION_EDIT} element={<Pages.CreditDecisionFormPage />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </AppWrapper>
    </div>
  )
}

export default App
